/**
 * Utility functions for date formatting
 */

/**
 * Format a date according to the specified format string
 * @param date - The date to format
 * @param format - The format string (e.g., 'MMM dd, yyyy HH:mm')
 * @returns Formatted date string
 */
export function formatDate(date: Date, format: string): string {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  try {
    // For the specific format 'MMM dd, yyyy HH:mm'
    if (format === 'MMM dd, yyyy HH:mm') {
      const options: Intl.DateTimeFormatOptions = {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      };
      
      return date.toLocaleString('en-US', options);
    }
    
    // For other formats, implement a simple formatter
    // This is a basic implementation that handles common format tokens
    return format.replace(/yyyy|MMM|MM|dd|HH|mm|ss/g, (token) => {
      switch (token) {
        case 'yyyy':
          return date.getFullYear().toString();
        case 'MMM':
          return date.toLocaleString('en-US', { month: 'short' });
        case 'MM':
          return (date.getMonth() + 1).toString().padStart(2, '0');
        case 'dd':
          return date.getDate().toString().padStart(2, '0');
        case 'HH':
          return date.getHours().toString().padStart(2, '0');
        case 'mm':
          return date.getMinutes().toString().padStart(2, '0');
        case 'ss':
          return date.getSeconds().toString().padStart(2, '0');
        default:
          return token;
      }
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
}

// Re-export the original formatDate function from formatDate.ts for backward compatibility
export { formatDate as formatDateSimple } from './formatDate';

/**
 * Format a date to YYYY-MM-DD format in UTC timezone
 * This ensures consistency between frontend and backend regardless of user's timezone
 * @param date - The date to format (defaults to current date)
 * @returns Date string in YYYY-MM-DD format (UTC)
 */
export function formatDateForAPI(date: Date = new Date()): string {
  return date.toISOString().split('T')[0];
}

/**
 * Format a date to YYYY-MM-DD format in the user's local timezone
 * Use this for display purposes when you want to show the user's local date
 * @param date - The date to format (defaults to current date)
 * @returns Date string in YYYY-MM-DD format (local timezone)
 */
export function formatDateLocal(date: Date = new Date()): string {
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
}

/**
 * Parse a date string from the backend (assumed to be in UTC) and convert to local display
 * @param dateString - Date string from backend (YYYY-MM-DD format)
 * @param useUTC - Whether to treat the input as UTC (default: true)
 * @returns Date object
 */
export function parseDateFromAPI(dateString: string, useUTC: boolean = true): Date {
  if (!dateString) {
    return new Date();
  }
  
  if (useUTC) {
    // Treat the date string as UTC midnight
    return new Date(`${dateString}T00:00:00Z`);
  } else {
    // Treat the date string as local midnight
    return new Date(`${dateString}T00:00:00`);
  }
}

/**
 * Get today's date in UTC format for API calls
 * @returns Today's date in YYYY-MM-DD format (UTC)
 */
export function getTodayUTC(): string {
  return formatDateForAPI(new Date());
}

/**
 * Get today's date in local timezone format for display
 * @returns Today's date in YYYY-MM-DD format (local timezone)
 */
export function getTodayLocal(): string {
  return formatDateLocal(new Date());
}

/**
 * Check if two dates are the same day (ignoring time)
 * @param date1 - First date
 * @param date2 - Second date
 * @param compareUTC - Whether to compare in UTC (default: true)
 * @returns True if dates are the same day
 */
export function isSameDay(date1: Date, date2: Date, compareUTC: boolean = true): boolean {
  if (compareUTC) {
    return formatDateForAPI(date1) === formatDateForAPI(date2);
  } else {
    return formatDateLocal(date1) === formatDateLocal(date2);
  }
}